import { useEffect, useRef, useState } from 'react';
import * as api from '@/api/client';

/**
 * Polls `GET /api/jobs/{job_id}/status` and surfaces the
 * standardised job-progress contract (Sub-Chunk 1 of the
 * claims-async migration). Generalised from the polling logic
 * previously inlined in `UploadedFilesList.tsx:JobProgressPanel`
 * so document ingestion + claims stages share one implementation.
 *
 * Terminal states that stop the poll loop:
 *   - finished              (RQ legacy — document ingestion)
 *   - succeeded             (stage_jobs canonical success)
 *   - failed                (RQ + stage_jobs)
 *   - refused_all_providers (every chain entry refused)
 *   - cancelled             (admin-initiated)
 *   - stopped               (RQ legacy)
 *
 * The hook starts polling automatically when `jobId` is set and
 * stops automatically on:
 *   - terminal status (per above)
 *   - jobId becomes null / undefined
 *   - component unmount
 *
 * Refresh-during-run reattach: callers persist the `job_id` in
 * local state / localStorage and re-mount the consumer with the
 * same id; the hook resumes polling against the same Redis +
 * stage_jobs record. See Stage3 for the persist pattern.
 */

interface UseJobStatusOptions {
  /** Poll cadence in ms. Default 2000 (2s) per Sub-Chunk 6.
   *  Document ingestion historically polled at 5s — pass
   *  `pollInterval: 5000` from JobProgressPanel to preserve
   *  that cadence. */
  pollInterval?: number;
  /** Fires once when the job reaches any terminal state. Used
   *  by callers that need to refetch the matter / reload the
   *  claims list. */
  onSettled?: (status: api.JobStatus) => void;
}

export interface UseJobStatusReturn {
  /** Latest status payload, or null until the first response. */
  status: api.JobStatus | null;
  /** Non-null when the most recent poll failed (network blip,
   *  404 from a deleted job, etc.). Errors do NOT stop the poll
   *  loop on their own — callers may want to keep trying. */
  error: string | null;
  /** True iff the status is one of the terminal values. Useful
   *  for callers that gate render on completion. */
  isTerminal: boolean;
  /** True when polling is active (jobId set and not yet
   *  terminal). */
  isPolling: boolean;
}

const TERMINAL_STATUSES = new Set([
  'finished',                // RQ legacy (document ingestion)
  'succeeded',               // stage_jobs canonical success
  'failed',
  'refused_all_providers',
  'cancelled',
  'stopped',
]);

function isTerminalStatus(s: string | null | undefined): boolean {
  return !!s && TERMINAL_STATUSES.has(s);
}

export function useJobStatus(
  jobId: string | null | undefined,
  options: UseJobStatusOptions = {},
): UseJobStatusReturn {
  const { pollInterval = 2000, onSettled } = options;
  const [status, setStatus] = useState<api.JobStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const settledRef = useRef(false);
  // Keep the onSettled callback in a ref so callers passing a
  // fresh closure each render don't reset the polling loop.
  const onSettledRef = useRef(onSettled);
  useEffect(() => {
    onSettledRef.current = onSettled;
  }, [onSettled]);

  useEffect(() => {
    if (!jobId) {
      // Reset state when the consumer clears the job id — a
      // re-launch reuses the hook instance and we don't want
      // stale data leaking through.
      setStatus(null);
      setError(null);
      settledRef.current = false;
      return;
    }

    let cancelled = false;
    settledRef.current = false;

    const tick = async () => {
      if (cancelled) return;
      try {
        const data = await api.getJobStatus(jobId);
        if (cancelled) return;
        setStatus(data);
        setError(null);
        if (isTerminalStatus(data.status) && !settledRef.current) {
          settledRef.current = true;
          if (onSettledRef.current) {
            try { onSettledRef.current(data); } catch { /* tolerate */ }
          }
        }
      } catch (e: any) {
        if (cancelled) return;
        // 404 from a job whose record has been reaped entirely
        // (RQ expired AND stage_jobs row deleted) — surface as
        // an error but don't crash the poll loop; the caller
        // chooses whether to keep polling.
        setError(e?.message || 'Status fetch failed');
      }
    };

    tick();
    const intervalHandle = setInterval(() => {
      if (settledRef.current) {
        clearInterval(intervalHandle);
        return;
      }
      tick();
    }, pollInterval);

    return () => {
      cancelled = true;
      clearInterval(intervalHandle);
    };
  }, [jobId, pollInterval]);

  const isTerminal = isTerminalStatus(status?.status);
  return {
    status,
    error,
    isTerminal,
    isPolling: !!jobId && !isTerminal,
  };
}
